<template>
  <div class="login-component animate__fadeIn">
    <div class="margin-auto center text-center flex">
      <div v-if="!logoSrc" class="logo center text-center">[Company logo]</div>
      <div v-else="" :style="logoStyle" class="logo center text-center"></div>
    </div>

    <h1
      class="main-title block text-center margin-xl-bottom l:margin-l-bottom text-2xl l:text-3xl">
      {{ stage === 0 ? tt(subTitle) : tt("user.login.ui.2fa.title") }}
    </h1>
    <div
      v-show="stage && stage > 0"
      class="progress-wrapper margin-m-bottom animate__fadeIn">
      <div
        class="progress-bar bg-gray-1 border-radius-l border-thin-gray-2 width-expand"
        style="height: 8px">
        <div
          class="progress-inner bg-alt border-radius-l"
          :style="`height: 100%; width: ${
            (currentTimer / (maxTimer != 0 ? maxTimer : 1)) * 100
          }%`"></div>
      </div>
    </div>
    <div v-if="shouldShowPrelogin" class="pre-login">
      <slot name="prelogin" :pre-login-complete="{ preLoginComplete }"></slot>
    </div>

    <div v-if="shouldShowForm" class="form-wrapper margin-l-bottom">
      <slot name="form">
        <div v-show="stage === 0" class="email-login-ui animate__fadeIn animate--faster">
          <base-form
            ref="emailLoginForm"
            v-model="emailPasswordLoginData"
            :action="emailLoginFormAction"
            method="post"
            :expected-fields="2"
            :config="emailLoginFormConfig"
            :general-error-message-403="'user.loginFailedGenetic'"
            :general-error-message="'user.loginFailedGenetic'"
            @form:submit-error="emailLoginErrorHandler"
            @form:submit-success="emailLoginSuccessHandler">
            <template #afterButtons></template>
          </base-form>
        </div>

        <div v-show="stage === 1" class="otp-ui animate__fadeIn animate--faster">
          <p class="text-center margin-2xl-bottom">
            {{ tu("user.login.ui.2fa.getTokenExplain") }}
          </p>

          <base-form
            ref="otpRequestForm"
            v-model="otpRequestFormData"
            :action="otpRequestFormAction"
            method="post"
            :expected-fields="2"
            :config="otpRequestFormConfig"
            :general-error-message="'user.loginFailedGenetic'"
            @form:submit-error="otpRequestErrorHandler"
            @form:submit-success="otpRequestSuccessHandler">
            <template #afterButtons></template>
          </base-form>
        </div>

        <div v-show="stage === 2" class="otp-ui animate__fadeIn animate--faster">
          <p class="text-center margin-2xl-bottom">
            {{ tu("user.login.ui.2fa.submitTokenExplain") }}
          </p>
          <base-form
            ref="otpSubmitForm"
            v-model="otpSubmitFormData"
            :action="otpSubmitFormAction"
            method="post"
            :expected-fields="2"
            :config="otpSubmitFormConfig"
            :general-error-message-403="'user.loginFailedGenetic'"
            :general-error-message="'user.loginFailedGenetic'"
            @form:submit-error="otpSubmitErrorHandler"
            @form:submit-success="otpSubmitSuccessHandler">
          </base-form>
        </div>

        <div v-show="stage === 3" class="finish-login-ui animate__fadeIn animate--faster">
          <div class="width-expand padding-2xl flex-middle flex-center">
            <spinner
              :size="2"
              text="user.login.ui.genericActionInProgress"
              class="text-3xl"></spinner>
          </div>
        </div>
      </slot>

      <slot name="afterForm"></slot>
    </div>

    <div class="login-footer-links">
      <slot name="footerLinks">
        <slot name="footer-links">
          <div class="login-footer-links-inner flex flex-between flex-wrap m:flex-nowrap">
            <div class="link-wrap margin-m-bottom l:margin-remove-bottom">
              <a href="#" @click.prevent.stop="startResetPasswordFlow">{{
                tu("user.resetPasswordCta")
              }}</a>
            </div>
            <div class="link-wrap">
              <saffron-link :to="{ name: 'index' }" class="">{{
                tu("core.backHome")
              }}</saffron-link>
            </div>
          </div>
        </slot>
      </slot>
    </div>

    <modal
      ref="resetPasswordModal"
      :show="false"
      :show-footer="false"
      title="user.login.ui.otp.resetPasswordTitle">
      <template #default>
        <password-resetter
          ref="resetPasswordComponent"
          @finished="handlePasswordReset"></password-resetter>
      </template>
    </modal>
  </div>
</template>

<script>
// todo: default texts, props etc
import PasswordResetter from "@/client/components/user/PasswordResetter";
import asyncOperations from "@/client/extensions/composition/asyncOperations.js";

export default {
  components: {
    PasswordResetter,
  },
  props: {
    /**
     * Where to redirect after login
     **/
    loginRedirect: {
      type: [String, Object, Function],
      default: "/",
    },
    /**
     * Where to redirect if activation is required
     **/
    activationRedirect: {
      type: [String, Object, Function],
      default: { name: "user-activation" },
    },
    /**
     * Show notifications explaining status
     */
    showNotifications: {
      type: Boolean,
      default: true,
    },
    subTitle: {
      type: String,
      default: "user.login.ui.title",
    },
    /**
     * Relative path for the logo
     * If false, will use placeholder
     **/
    logoSrc: {
      type: [String, Boolean],
      default: "core/images/logos/main.png",
    },
    /**
     * Api form source (url to get the form)
     **/
    formSource: {
      type: String,
      default: "form/user-login",
    },
    /**
     * Where does the login form post to. relative API path
     **/
    emailLoginFormAction: {
      type: String,
      default: "/token/auth",
    },
    otpRequestFormAction: {
      type: String,
      default: "token/otp",
    },
    showPrelogin: {
      type: [Boolean],
      default: true,
    },
    showForm: {
      type: [Boolean],
      default: true,
    },
    otpSubmitFormAction: {
      type: String,
      default: "token/auth",
    },
  },
  emits: ["login:success"],
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  data: function () {
    let identifierFields = {
      email: {
        name: "identifier",
        icon: "mail",
        type: "email",
        label: "user.login.ui.otp.identifierFieldEmailLabel",
        placeholder: "user.login.ui.otp.identifierFieldEmailPlaceholder",
        validation: {
          required: {},
          email: {},
        },
      },
      whatsapp: {
        name: "identifier",
        icon: "iphone",
        type: "i18nPhoneNumber",
        label: "user.login.ui.otp.identifierFieldWhatsappLabel",
        placeholder: "user.login.ui.otp.identifierFieldWhatsappPlaceholder",
        validation: {
          required: {},
          i18nPhoneNumberObject: {},
        },
      },
      sms: {
        name: "identifier",
        icon: "iphone",
        type: "i18nPhoneNumber",
        label: "user.login.ui.otp.identifierFieldSmsLabel",
        placeholder: "user.login.ui.otp.identifierFieldSmsPlaceholder",
        validation: {
          required: {},
          i18nPhoneNumberObject: {},
        },
      },
    };

    let listMediumField = {
      name: "medium",
      label: "user.login.ui.otp.mediumLabel",
      legend: "user.login.ui.otp.mediumLegend",
      type: "select",
      multiple: false,
      list: [
        { value: "email", label: "user.login.ui.otp.medium.email" },
        { value: "sms", label: "user.login.ui.otp.medium.sms" },
        { value: "whatsapp", label: "user.login.ui.otp.medium.whatsapp" },
      ],
    };
    let singleOptionMediumField = {
      name: "medium",
      label: false,
      type: "hidden",
      disabled: true,
    };
    return {
      singleOptionMediumField,
      listMediumField,
      otpMediaTypes: [],
      timerInterval: null,
      stage: 0,
      preLoginComplete: false,
      emailPasswordLoginData: {
        email: "",
        password: "",
        type: "emailPassword",
      },
      otpRequestFormData: {
        medium: "email",
        identifier: null,
      },
      otpSubmitFormData: {
        verification: null,
        type: "otp",
        otp: null,
        medium: "email",
        subjectUuid: null,
      },
      formContext: "context2",
      emailLoginFormConfig: {
        fields: {
          email: {
            name: "email",
            type: "email", // must have default
            label: "core.form.email",
            placeholder: "core.form.placeholders.email",
            icon: "mail",
            validation: {
              required: {},
              email: {},
            },
          },
          password: {
            name: "password",
            type: "password", // must have default
            label: "core.form.password",
            placeholder: "core.form.placeholders.password",
            icon: "lock",
            defaultValue: "",
            validation: {
              required: {},
              minLength: {
                params: { min: 6 },
              },
            },
          },
          type: {
            name: "type",
            type: "hidden",
            disabled: true,
            defaultValue: "emailPassword",
          },
        },
        formHandling: {},
      },
      identifierFields: identifierFields,
      otpRequestFormConfig: {
        fields: {
          medium: listMediumField,
          identifier: identifierFields.email,
        },
      },
      otpSubmitFormConfig: {
        fields: {
          otp: {
            name: "otp",
            type: "text",
            icon: "lock",
            label: "user.login.ui.otp.otpLabel",
            placeholder: "user.login.ui.otp.otpPlaceholder",
            validation: {
              required: {},
            },
          },
        },
      },
      tokens: {},
      currentTimer: 0,
      maxTimer: 300,
      subjectUuid: null,
      asyncData: {
        otpMediaTypes: {
          target: "token/otp/media",
          data: {},
          options: {
            dataMutator: (res) => {
              let list = Object.keys(res.media);
              return list;
            },
          },
        },
      },
    };
  },
  computed: {
    isFetchingProfile() {
      return this.$store.getters["user/fetchingProfile"];
    },
    logoStyle() {
      if (!this.logoSrc) {
        return [];
      }

      let url = utilities.requireAsset(this.logoSrc);
      return {
        backgroundImage: "url('" + url + "')",
        height: "80px",
        width: "80px",
      };
    },
    shouldShowPrelogin() {
      return this.showPrelogin && !this.preLoginComplete;
    },
    shouldShowForm() {
      return this.showForm || this.preLoginComplete;
    },
    translatedSubTitle() {
      return this.safeTranslate(this.subTitle);
    },
  },
  watch: {
    preLoginComplete(newVal, oldVal) {},
    "otpRequestFormData.medium": {
      handler(newVal) {
        if (!newVal || newVal === "") {
          return;
        }
        this.otpRequestFormConfig.fields.identifier = this.identifierFields[newVal];
        this.otpRequestFormData.identifier = undefined;
        this.otpSubmitFormData.subjectUuid = newVal;
      },
    },
    currentTimer(newVal) {
      if (newVal && newVal > 0 && newVal >= this.maxTimer) {
        this.reset();
        this.$s.ui.notification("user.login.ui.processTimedOutNotification", "warning");
      }
    },
    otpMediaTypes(newVal) {
      console.log("new val for media list", newVal);

      if (newVal.length === 1) {
        this.otpRequestFormConfig.fields.medium = this.singleOptionMediumField;
        this.otpRequestFormData.medium = newVal[0];
      } else {
        this.otpRequestFormConfig.fields.medium = this.listMediumField;
        let list = [];
        newVal.forEach( (key) => {
          list.push({
            value: key,
            label: `user.login.ui.otp.medium.${key}`
          });
        });
        this.otpRequestFormConfig.fields.medium.list = list;
        this.otpRequestFormData.medium = newVal[0];
      }
    },
  },
  created() {},
  mounted() {
    this.emailPasswordLoginData = {
      //  email: "propush@gmail.com",
      // password: "12345678",
      type: "emailPassword",
    };
    // this.$refs.emailLoginForm.submit();
  },
  methods: {
    startResetPasswordFlow() {
      this.$refs.resetPasswordModal.open();
    },
    handlePasswordReset(eventData) {
      this.stage = 0;
      this.$s.ui.notification("user.resetPassword.canLoginWithNewPassword", "success");
      //set email password
      this.$refs.resetPasswordModal.close();

      this.setCredentials({
        email: eventData.email,
        password: eventData.password,
      });

      this.loginByState();
    },
    ourFormCallback() {},
    testMethod(test) {
      console.log("test method!", test);
    },
    setCredentials(credentials) {
      this.emailPasswordLoginData.password = credentials.password;
      this.emailPasswordLoginData.email = credentials.email;
      return true;
    },
    loginByState() {
      warn("login by state not implemented for 2fa");
      //this.$refs.loginForm.submit();
    },
    async emailLoginSuccessHandler(eventData) {
      let data = eventData.resultData;
      this.currentTimer = 0;
      this.maxTimer = data.lifeTime;
      this.subjectUuid = data.subjectUuid;
      this.tokens.emailPassword = data.token;
      this.otpSubmitFormData.subjectUuid = data.subjectUuid;
      this.stage = 1;

      if (this.otpRequestFormConfig?.fields?.identifier?.type === "email") {
        this.otpRequestFormData.identifier = this.emailPasswordLoginData.email;
      }
      this.resetTimer(true);
      this.startTimer();
    },
    async activationRequiredHandler(eventData) {
      if (typeof this.activationRedirect === "function") {
        this.$router.push(await this.activationRedirect());
        return true;
      }

      this.$router.push(this.activationRedirect);
    },
    async otpRequestErrorHandler() {
      this.$s.ui.notification("user.login.ui.genericError", "error");
    },
    async otpSubmitErrorHandler() {
      this.$s.ui.notification("user.login.ui.genericError", "error");
    },
    async otpRequestSuccessHandler(result) {
      this.stage = 2;
      this.otpSubmitFormData.verification = result.resultData.verification;
      // if server in dev mode
      if (result.resultData.otp) {
        this.otpSubmitFormData.otp = result.resultData.otp;
      }
    },
    async otpSubmitSuccessHandler(result) {
      this.stage = 3;
      this.tokens.otp = result.resultData.token;
      this.finishLoginByState();
    },
    async finishLoginByState() {
      let url = "token/refresh/nfa";
      let payload = {
        tokens: Object.values(this.tokens),
      };
      let options = {
        method: "post",
      };

      let result = await this.asyncOps.asyncCall(url, payload, options);

      if (result.hasError) {
        this.$s.ui.notification("user.login.ui.loginProcessFailedNotification", "error");
        this.reset();
        return false;
      }

      this.loginByCreateRefreshTokenResult(result.data);
    },
    async loginByCreateRefreshTokenResult(tokenData) {
      this.$store.commit("user/setToken", tokenData.jwt);
      this.$store.commit("user/setRefreshToken", tokenData.refreshToken);

      this.$store.commit("user/setRefreshTokenExpires", tokenData.refreshTokenExpires);

      let result = await this.$store.dispatch("user/updateUserProfile");

      if (result === false) {
        this.$saffron.ui.notification(
          this.safeTranslate("user.fetchProfileException"),
          "danger"
        );
        return false;
      }

      this.$emit("login:success");

      if (this.showNotifications) {
        this.$s.ui.notification("core.user.loginCompleteNotification", "success");
      }

      // we are logged in and are redirecting
      // in case no router redirect provided
      if (!this.$route || !this.$route.query || !this.$route.query.redirect) {
        if (this.loginRedirect) {
          this.$router.push(this.loginRedirect);
        }

        return true;
      }

      // we have route query, dictating the redirect
      let params = this.$route.query.redirectParams
        ? this.$route.query.redirectParams
        : {};
      let resolvedRoute = false;
      try {
        resolvedRoute = this.$router.resolve({ name: this.$route.query.redirect });
      } catch (e) {}

      // try to resolve the name
      if (resolvedRoute) {
        // case of route name
        this.$router.push({ name: this.$route.query.redirect, params });
      } else {
        // case of plain string
        this.$router.push(this.$route.query.redirect);
      }
    },
    async emailLoginErrorHandler(eventData) {
      let res = eventData.result;

      if (res.data === 403) {
        this.$nextTick(() => {
          this.$refs["loginForm"].setGeneralErrorMessage("core.user.credentialsRejected");
        });
        // login rejected
      }

      if (res.data === 412) {
        this.activationRequiredHandler(eventData);
      }
    },
    startTimer() {
      this.stopTimer();
      this.timerInterval = setInterval(() => {
        this.currentTimer = this.currentTimer + 1;
      }, 1000);
    },
    resetTimer(stopTimer = false) {
      this.currentTimer = 0;
      if (stopTimer) {
        this.stopTimer();
      }
    },
    stopTimer() {
      clearInterval(this.timerInterval);
    },
    reset() {
      this.stopTimer();
      this.maxTimer = 0;
      this.stage = 0;
    },
  },
};
</script>

<style scoped lang="scss">
.logo {
  flex: auto;
  height: 100px;
  width: auto;
  background-size: auto 100%;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: var(--margin-xl);

  @media (max-width: 767px) {
    height: 60px;
    width: auto;
    background-size: auto 60%;
    margin-bottom: var(--margin-m);
  }
}

.login-footer-links-inner {
  @media (max-width: 767px) {
    flex-wrap: wrap;
  }
}

.link-wrap {
  &:last-of-type {
    @media (max-width: 767px) {
      margin-bottom: 0 !important;
    }
  }
}

.login-component {
  max-width: calc(90vw - 60px);
  width: 550px;
  flex: 550px;

  @media screen and (max-width: 640px) {
    max-width: calc(90vw - 60px);
    width: 80vw;
    flex: 80vw;
  }

  @media screen and (max-width: 400px) {
    max-width: calc(90vw - 20px);
    width: 90vw;
    flex: 90vw;
  }

  .link-wrap {
    @media screen and (max-width: 640px) {
      flex: 100%;
    }
  }
}
</style>
